import { useMemo, useState } from 'react'
import { AlertCircle, Check, CircleCheck } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import type { LisMappingFailure } from '@/api/lisConfigApi'
import { useLisMappingFailures, useResolveLisFailure } from '@/hooks/useLisConfig'

/** Every reason the mapper records, with what a reader should do about it. */
const REASON_LABEL: Record<string, string> = {
  no_configuration: 'Nothing configured to read this',
  no_mapping: 'No pre-map matched the message',
  no_payload: 'The delivered message was not retained',
  no_order: 'Stage needed, no order to take it from',
  no_submission: 'No open form to write into',
  submission_locked: 'The form is completed or cancelled',
  unknown_form: 'The mapped form no longer exists',
  no_pre_map: 'The pre-map is switched off or gone',
  ambiguous_match: 'Several entries matched the keys',
  no_value: 'The value field was empty on the matched entry',
  value_not_scalar: 'The value field holds a list or an object',
}

const ALL = '__all__'

/**
 * **Map error logs** — results that arrived and were not written anywhere.
 *
 * Its own tab rather than a panel on the configuration screen, because it is a
 * log and not configuration, and because a failure that only appears once
 * someone opens a config tab is close to not appearing at all. An empty form
 * field and a test nobody ran look identical from the ward; this is the only
 * thing that tells them apart.
 *
 * Resolving a row marks it, never deletes it. The log is a record.
 */
export function LisMapErrorsTab() {
  const [showResolved, setShowResolved] = useState(false)
  const [reason, setReason] = useState<string>(ALL)
  const failures = useLisMappingFailures(showResolved)
  const resolve = useResolveLisFailure()

  const rows = useMemo(() => {
    const all = failures.data ?? []
    const filtered = reason === ALL ? all : all.filter((f) => f.reason === reason)
    // Unresolved first — they are the ones somebody still has to act on.
    return [...filtered].sort((a, b) => {
      if (a.resolved !== b.resolved) return a.resolved ? 1 : -1
      return b.occurredAt.localeCompare(a.occurredAt)
    })
  }, [failures.data, reason])

  const unresolved = (failures.data ?? []).filter((f) => !f.resolved).length
  const reasonsPresent = [...new Set((failures.data ?? []).map((f) => f.reason))]

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-center gap-3'>
        <Select value={reason} onValueChange={setReason}>
          <SelectTrigger className='w-72'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All reasons</SelectItem>
            {reasonsPresent.map((r) => (
              <SelectItem key={r} value={r}>
                {REASON_LABEL[r] ?? r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <label className='flex items-center gap-2 text-sm'>
          <input
            type='checkbox'
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
          />
          Show resolved
        </label>

        {unresolved > 0 ? (
          <Badge variant='destructive'>{unresolved} unresolved</Badge>
        ) : null}
      </div>

      {failures.isPending ? (
        <p className='text-sm text-muted-foreground'>Loading…</p>
      ) : failures.isError ? (
        <p className='text-sm text-destructive'>Could not load the error log.</p>
      ) : rows.length === 0 ? (
        // Said plainly rather than an empty table: "nothing failed" and "nothing
        // was checked" must not look the same.
        <p className='flex items-center gap-2 rounded-xl border bg-white p-6 text-sm text-muted-foreground shadow-sm'>
          <CircleCheck className='h-4 w-4 text-emerald-600' />
          {showResolved || reason !== ALL
            ? 'Nothing matches this filter.'
            : 'Every result received so far was written where it was configured to go.'}
        </p>
      ) : (
        <div className='overflow-x-persistent rounded-xl border bg-white shadow-sm'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b text-left text-[11px] uppercase tracking-wider text-muted-foreground'>
                <th className='px-4 py-2 font-medium'>When</th>
                <th className='px-4 py-2 font-medium'>Reason</th>
                <th className='px-4 py-2 font-medium'>Test / value</th>
                <th className='px-4 py-2 font-medium'>Order</th>
                <th className='px-4 py-2 font-medium'>Patient</th>
                <th className='px-4 py-2 font-medium'>Target</th>
                <th className='px-4 py-2 font-medium'>What to do</th>
                <th className='px-4 py-2 font-medium'></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => (
                <FailureRow key={f.id} failure={f} onResolve={() => resolve.mutate(f.id)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function FailureRow({
  failure,
  onResolve,
}: {
  failure: LisMappingFailure
  onResolve: () => void
}) {
  return (
    <tr className={cn('border-b last:border-0 align-top', failure.resolved && 'opacity-55')}>
      <td className='whitespace-nowrap px-4 py-2 text-xs text-muted-foreground'>
        {new Date(failure.occurredAt).toLocaleString()}
      </td>
      <td className='px-4 py-2'>
        <span className='flex items-start gap-1.5 text-xs'>
          {failure.resolved ? null : (
            <AlertCircle className='mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive' />
          )}
          {REASON_LABEL[failure.reason] ?? failure.reason}
        </span>
      </td>
      <td className='px-4 py-2 font-mono text-xs'>
        {failure.testCode}
        {failure.preMapLabel ? ` / ${failure.preMapLabel}` : ''}
        {failure.valuePath ? (
          <span className='text-muted-foreground'> · {failure.valuePath}</span>
        ) : null}
      </td>
      <td className='px-4 py-2 font-mono text-xs'>{failure.orderId ?? '—'}</td>
      <td className='px-4 py-2 font-mono text-xs'>{failure.mrn ?? '—'}</td>
      <td className='px-4 py-2 font-mono text-xs'>{failure.targetFieldId ?? '—'}</td>
      {/* The backend writes this to be read by a person — rendered as sent. */}
      <td className='px-4 py-2 text-xs text-muted-foreground'>{failure.detail}</td>
      <td className='px-4 py-2 text-right'>
        {failure.resolved ? (
          <span className='text-[11px] text-muted-foreground'>Resolved</span>
        ) : (
          <Button size='sm' variant='outline' onClick={onResolve}>
            <Check className='mr-1 h-3.5 w-3.5' />
            Done
          </Button>
        )}
      </td>
    </tr>
  )
}
